<!-- Prose.vue -->
<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { Button } from '@/components/ui/button'

import { formatMdText } from '@/lib/utils/format.js'

// Define props with validation
const props = defineProps({
  // HTML content to display
  html: {
    type: String,
    default: null,
  },
  // Markdown content to display
  md: {
    type: String,
    default: null,
  },
  // Maximum height before truncation (0 means no limit)
  maxHeight: {
    type: Number,
    default: 0,
  },
})

// Component state
const contentRef = ref(null)
const isExpanded = ref(false)
const hasOverflow = ref(false)
const containerHeight = ref('auto')
const displayContent = ref('')

// Computed style for the content container
const containerStyle = computed(() => ({
  maxHeight: isExpanded.value
    ? 'none'
    : props.maxHeight
      ? `${props.maxHeight}px`
      : 'none',
  overflow: 'hidden',
  position: 'relative',
}))

// Process content based on props
function processContent() {
  if (props.html !== null) {
    // If HTML is provided, use it directly
    displayContent.value = props.html
  }
  else if (props.md !== null) {
    // If Markdown is provided, convert it to HTML
    displayContent.value = formatMdText(props.md)
  }
  else {
    // Log error if neither HTML nor Markdown is provided
    console.error('Prose component: Neither html nor md prop was provided')
    displayContent.value = ''
  }
}

// Check if content overflows and needs the "View more" button
function checkOverflow() {
  if (!contentRef.value || !props.maxHeight) {
    hasOverflow.value = false
    return
  }

  const element = contentRef.value
  hasOverflow.value = element.scrollHeight > props.maxHeight

  // Set initial container height
  containerHeight.value = isExpanded.value
    ? 'auto'
    : `${Math.min(element.scrollHeight, props.maxHeight)}px`
}

// Toggle expanded state
function toggleExpand() {
  isExpanded.value = !isExpanded.value

  if (isExpanded.value) {
    containerHeight.value = `${contentRef.value.scrollHeight}px`
  }
  else {
    containerHeight.value = `${props.maxHeight}px`
  }
}

// Watch for changes in content or maxHeight
watch(
  () => props.html,
  () => {
    processContent()
    checkOverflow()
  },
)

watch(
  () => props.md,
  () => {
    processContent()
    checkOverflow()
  },
)

watch(() => props.maxHeight, checkOverflow)

// Initialize on mount
onMounted(() => {
  processContent()
  checkOverflow()
})
</script>

<template>
  <div class="prose-container">
    <!-- Content container with dynamic height -->
    <div
      ref="contentRef"
      class="prose"
      :style="containerStyle"
      :class="{ 'has-fade': hasOverflow && !isExpanded }"
    >
      <div v-html="displayContent" />
    </div>

    <!-- View more button -->
    <div
      v-if="hasOverflow"
      class="view-more-container"
      :class="{ 'with-fade': !isExpanded }"
    >
      <Button
        variant="secondary"
        class="view-more-button"
        @click="toggleExpand"
      >
        {{ isExpanded ? "Voir moins" : "Voir plus" }}
      </Button>
    </div>
  </div>
</template>

<style scoped>
.prose-container {
  position: relative;
  width: 100%;
}

.prose {
  transition: max-height 0.3s ease-in-out;
}

/* Fade effect for truncated content */
.has-fade::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100px;

  /*  background: linear-gradient(to bottom, transparent, rgb(var(--bg)));
*/
  background: linear-gradient(to bottom, transparent, rgb(255, 255, 255));

  pointer-events: none;
}

.view-more-container {
  display: flex;
  justify-content: center;
  margin-top: 1rem;
  position: relative;
}

.with-fade {
  margin-top: -60px;
  position: relative;
  z-index: 10;
}

/* Inherit prose styles from the parent component */
:deep(.prose) {
  max-width: none;
  font-size: 1.125rem;
  line-height: 1.8;
}

:deep(.prose p) {
  margin-bottom: 1.5em;
}

:deep(.prose h1) {
  font-size: 2.5rem;
  margin-top: 2em;
  margin-bottom: 1em;
}

:deep(.prose h2) {
  font-size: 2rem;
  margin-top: 1.8em;
  margin-bottom: 0.8em;
}

:deep(.prose h3) {
  font-size: 1.5rem;
  margin-top: 1.6em;
  margin-bottom: 0.6em;
}

:deep(.prose a) {
  @apply text-primary underline decoration-2 underline-offset-2;
}

:deep(.prose code) {
  @apply bg-muted px-2 py-1 rounded-md text-sm;
}

:deep(.prose pre) {
  @apply bg-muted border p-4 rounded-lg overflow-x-auto;
}

:deep(.prose blockquote) {
  @apply border-l-4 border-muted pl-4 italic text-muted-foreground;
}

:deep(.prose ul) {
  @apply list-disc pl-6;
}

:deep(.prose ol) {
  @apply list-decimal pl-6;
}

:deep(.prose li) {
  @apply my-2;
}
</style>
